import { isUUID } from '@frameio/utils/formatters';
import { createUrl } from '../helpers/createUrl';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

export type WorkspacesGetLinkParams =
  /**
   * WorkspacesPage
   * Show the list of workspaces for the last viewed account.
   * / (root path)
   *
   * Note: we don't currently have a path to directly address a single workspace (only a project)
   *
   */
  | {
      page: 'workspaces';
      accountId?: string;
    }
  /**
   * /workspaces/:workspaceId
   */
  | {
      page: 'workspaces';
      workspaceId?: string;
      explicitWorkspacesPath?: boolean;
    };

export const getLinkForWorkspaces = (
  params: WorkspacesGetLinkParams,
  explicitWorkspacesPath?: boolean
): ValidHref => {
  if ('workspaceId' in params && params.workspaceId) {
    return createUrl({ pathname: `/workspaces/${params.workspaceId}` });
  }
  const isExplicitPath =
    explicitWorkspacesPath === true ||
    ('explicitWorkspacesPath' in params &&
      params.explicitWorkspacesPath === true);
  const pathname: ValidHref = isExplicitPath ? '/workspaces' : '/';
  const searchParams: { a?: string } = {};

  // ... ?a=:accountId
  if ('accountId' in params && params.accountId) {
    searchParams.a = toCondensedUUID(params.accountId);
  }

  return createUrl({
    pathname,
    searchParams,
  });
};

export const isWorkspacesPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');

  if (parts[1] === 'workspaces' && parts.length === 3 && isUUID(parts[2])) {
    return true;
  }

  return pathname === '/' || pathname === '/workspaces';
};

/**
 * Returns true only for singular workspace pages (/workspaces/:id) so we can
 * differentiate between it and the umbrella (plural) workspaces page type.
 */
export const isWorkspacePageType = (href: ValidHref | string): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');

  return parts[1] === 'workspaces' && parts.length === 3 && isUUID(parts[2]);
};
